import { EKeyInitiativeGoalMode } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Tooltip } from 'core-components/library/tooltip'
import { getKeyInitiativeStatusTranslationKey } from 'partner360/entities/keyInitiatives/model/status'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'

import { getFinGoalTypeTranslationKey } from '../../helpers/getFinGoal'
import { EMPTY_LIST_VALUE } from '../../helpers/listDictionaries'
import { toggleListFilterNode } from '../../helpers/listFilters'
import { IListFilterNode, TListFilterDimension, TListFilters } from '../../models'

import styles from './ListFilters.module.scss'

export const LIST_FILTER_LABELS: Record<TListFilterDimension, string> = {
  strategicInitiatives: TRANSLATION_KEYS.listView.groupBy.strategicInitiative,
  businessPriorities: TRANSLATION_KEYS.listView.groupBy.businessPriority,
  businessUnits: TRANSLATION_KEYS.listView.businessUnit,
  periods: TRANSLATION_KEYS.dashboard.table.columns.period,
  statuses: TRANSLATION_KEYS.dashboard.table.columns.status,
  goalModes: TRANSLATION_KEYS.listView.goalMode,
  fundingSources: TRANSLATION_KEYS.listView.fundingSource,
  countryCodes: TRANSLATION_KEYS.dashboard.filters.geo,
}

export const getListFilterLabel = (label: string, dimension: TListFilterDimension): string => {
  if (label === EMPTY_LIST_VALUE) return TRANSLATION_KEYS.listView.notSpecified
  if (dimension === 'goalModes') {
    const mode = Object.values(EKeyInitiativeGoalMode).find((value) => value === label)
    return mode ? getFinGoalTypeTranslationKey(mode) : label
  }
  if (dimension === 'statuses') return getKeyInitiativeStatusTranslationKey(label)
  if (dimension === 'periods' && label === 'full-year')
    return TRANSLATION_KEYS.listView.period.fullYear
  return label
}

interface IListFiltersProps {
  filters: TListFilters
  options: Record<TListFilterDimension, IListFilterNode[]>
  onChange: (dimension: TListFilterDimension, values: string[]) => void
  onClear: () => void
  visibleCount: number
  totalCount: number
  isRollupMode: boolean
}

const FilterNodes = ({
  nodes,
  dimension,
  selected,
  onChange,
}: {
  nodes: IListFilterNode[]
  dimension: TListFilterDimension
  selected: string[]
  onChange: (values: string[]) => void
}) => (
  <ul className={styles.tree}>
    {nodes.map((node) => {
      const selectedCount = node.values.filter((value) =>
        selected.some((item) =>
          dimension === 'businessPriorities'
            ? item.toLowerCase() === value.toLowerCase() || item === node.label
            : item === value
        )
      ).length
      return (
        <li key={node.id}>
          <Checkbox
            size="sm"
            className={styles.option}
            checked={selectedCount > 0 && selectedCount === node.values.length}
            indeterminate={selectedCount > 0 && selectedCount < node.values.length}
            onChange={(checked) =>
              onChange(
                toggleListFilterNode(
                  selected.filter(
                    (value) =>
                      dimension !== 'businessPriorities' ||
                      (value !== node.label &&
                        !node.values.some((item) => item.toLowerCase() === value.toLowerCase()))
                  ),
                  node.values,
                  checked
                )
              )
            }
            label={
              <span className={styles.optionLabel}>
                <Translation text={getListFilterLabel(node.label, dimension)} span />
                <span className={styles.count}>{node.count}</span>
              </span>
            }
          />
          {Boolean(node.children?.length) && (
            <FilterNodes
              nodes={node.children ?? []}
              dimension={dimension}
              selected={selected}
              onChange={onChange}
            />
          )}
        </li>
      )
    })}
  </ul>
)

export const ListFilters = ({
  filters,
  options,
  onChange,
  onClear,
  visibleCount,
  totalCount,
  isRollupMode,
}: IListFiltersProps) => {
  const [search, setSearch] = useState('')
  const t = useSafetyTranslation()
  const searchOptions = useMemo(() => {
    const term = search.trim().toLocaleLowerCase()
    const filterNodes = (
      nodes: IListFilterNode[],
      dimension: TListFilterDimension
    ): IListFilterNode[] =>
      nodes.flatMap((node) => {
        if (
          !term ||
          t(getListFilterLabel(node.label, dimension)).toLocaleLowerCase().includes(term)
        )
          return [node]
        const children = filterNodes(node.children ?? [], dimension)
        return children.length ? [{ ...node, children }] : []
      })
    return (Object.keys(options) as TListFilterDimension[])
      .map((dimension) => ({ dimension, nodes: filterNodes(options[dimension], dimension) }))
      .filter(({ dimension }) => dimension !== 'countryCodes' || isRollupMode)
  }, [options, search, t, isRollupMode])
  const activeCount = Object.values(filters).reduce((sum, values) => sum + values.length, 0)
  return (
    <div
      className={styles.root}
      role="dialog"
      aria-label={t(TRANSLATION_KEYS.dashboard.filters.title)}
    >
      <label className={styles.search}>
        <span className={styles.searchLabel}>
          <Translation text={TRANSLATION_KEYS.listView.search} span />
        </span>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t(TRANSLATION_KEYS.listView.search)}
        />
        <i className="kbase kbase-search" aria-hidden="true" />
      </label>
      <div className={styles.sections}>
        {searchOptions.map(({ dimension, nodes }, index) =>
          nodes.length ? (
            <section
              key={dimension}
              className={index > 1 ? styles.secondarySection : styles.section}
            >
              <div className={styles.sectionHeader}>
                <h3>
                  <Translation text={LIST_FILTER_LABELS[dimension]} span />
                </h3>
                {filters[dimension].length > 0 && (
                  <Button variant="text" size="sm" onClick={() => onChange(dimension, [])}>
                    <Translation text={TRANSLATION_KEYS.listView.clearSection} />
                  </Button>
                )}
              </div>
              <FilterNodes
                nodes={nodes}
                dimension={dimension}
                selected={filters[dimension]}
                onChange={(values) => onChange(dimension, values)}
              />
            </section>
          ) : null
        )}
        {!searchOptions.some(({ nodes }) => nodes.length) && (
          <div className={styles.empty}>
            <Translation text={TRANSLATION_KEYS.listView.noFilterMatches} />
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Translation
          text={TRANSLATION_KEYS.listView.showing}
          replaceParams={[
            { key: '{count}', value: String(visibleCount) },
            { key: '{total}', value: String(totalCount) },
          ]}
        />
        {activeCount > 0 && (
          <Button variant="text" onClick={onClear}>
            <Translation text={TRANSLATION_KEYS.listView.clearAll} />
          </Button>
        )}
      </div>
    </div>
  )
}

const flattenNodes = (nodes: IListFilterNode[]): IListFilterNode[] =>
  nodes.flatMap((node) => [node, ...flattenNodes(node.children ?? [])])

export const ActiveListFilters = ({
  filters,
  options,
  onChange,
  onClear,
  disabled = false,
}: Pick<IListFiltersProps, 'filters' | 'options' | 'onChange' | 'onClear'> & {
  disabled?: boolean
}) => {
  const t = useSafetyTranslation()
  const active = (Object.keys(filters) as TListFilterDimension[]).flatMap((dimension) =>
    filters[dimension].map((value) => {
      const nodes = flattenNodes(options[dimension])
      const option =
        nodes.find(
          (node) => node.label === value || node.id.toLowerCase() === value.toLowerCase()
        ) ?? nodes.find((node) => node.values.length === 1 && node.values.includes(value))
      return { dimension, value, label: getListFilterLabel(option?.label ?? value, dimension) }
    })
  )
  if (!active.length) return null
  return (
    <div className={styles.activeFilters}>
      {active.map(({ dimension, value, label }) => (
        <span className={styles.tag} key={JSON.stringify([dimension, value])}>
          <Translation text={label} span />
          <Tooltip
            inline
            tipContent={() => <Translation text={TRANSLATION_KEYS.listView.removeFilter} />}
          >
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange(
                  dimension,
                  filters[dimension].filter((item) => item !== value)
                )
              }
              aria-label={t(TRANSLATION_KEYS.listView.removeFilter) + ': ' + t(label)}
            >
              <i className="kbase kbase-close" aria-hidden="true" />
            </button>
          </Tooltip>
        </span>
      ))}
      <Button variant="text" onClick={onClear} disabled={disabled}>
        <Translation text={TRANSLATION_KEYS.listView.clearAll} />
      </Button>
    </div>
  )
}
